import React, { useState, useEffect } from 'react';
import { Users, QrCode, Clock, CheckCircle, CreditCard, Play, Phone, Mail, FileText, AlertCircle, Loader2, RefreshCw, Eye, UserCheck, XCircle, Plus, Calendar, User, ChevronRight, Stethoscope, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckInManager = () => {
  const [loading, setLoading] = useState(true);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [waitingRoom, setWaitingRoom] = useState([]);
  const [qrCode, setQrCode] = useState(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [patientData, setPatientData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('today'); // today, waiting
  
  // Nuovi stati per funzionalità aggiuntive
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyPatient, setEmergencyPatient] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    reason: 'Visita urgente',
    services: [],
    doctor_id: '',
    room: '',
    include_emergency_fee: true
  });
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatientProfile, setSelectedPatientProfile] = useState(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [patientAppointments, setPatientAppointments] = useState([]);
  
  // Nuovi stati per prestazioni e medici
  const [availableServices, setAvailableServices] = useState([]);
  const [availableDoctors, setAvailableDoctors] = useState([]);
  const [emergencyResult, setEmergencyResult] = useState(null);
  const [showQrResultModal, setShowQrResultModal] = useState(false);

  useEffect(() => {
    loadData();
    loadServicesAndDoctors();
    // Polling ogni 15 secondi per la sala d'attesa
    const interval = setInterval(loadWaitingRoom, 15000);
    return () => clearInterval(interval);
  }, []);

  const loadServicesAndDoctors = async () => {
    try {
      const token = localStorage.getItem('token');
      const [servicesRes, doctorsRes] = await Promise.all([
        axios.get(`${API}/prestazioni`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/doctors-available`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      // Use all_services if available, otherwise flatten from listino
      let allServices = servicesRes.data.all_services || [];
      
      if (allServices.length === 0 && servicesRes.data.listino) {
        Object.values(servicesRes.data.listino).forEach(category => {
          if (category.services) {
            category.services.forEach(s => {
              allServices.push({...s, category: category.title});
            });
          }
        });
      }
      
      setAvailableServices(allServices);
      setAvailableDoctors(doctorsRes.data.doctors || []);
    } catch (error) {
      console.error('Error loading services/doctors:', error);
    }
  };

  const loadData = async () => {
    await Promise.all([loadTodayAppointments(), loadWaitingRoom(), loadQrCode()]);
    setLoading(false);
  };

  const loadTodayAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/today-appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTodayAppointments(res.data.appointments || []);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const loadWaitingRoom = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/waiting-room`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaitingRoom(res.data.waiting_room || []);
    } catch (error) {
      console.error('Error loading waiting room:', error);
    }
  };

  const loadQrCode = async () => {
    try {
      const res = await axios.get(`${API}/clinic/qr-code`);
      setQrCode(res.data);
    } catch (error) {
      console.error('Error loading QR code:', error);
    }
  };

  const openManualCheckin = (apt) => {
    setSelectedAppointment(apt);
    setPatientData({
      first_name: apt.patient?.first_name || '',
      last_name: apt.patient?.last_name || '',
      phone: apt.patient?.phone || '',
      email: apt.patient?.email || '',
      fiscal_code: apt.patient?.fiscal_code || '',
      birth_date: apt.patient?.birth_date || '',
      address: apt.patient?.address || ''
    });
    setShowCheckinModal(true);
  };

  const handleManualCheckin = async () => {
    if (!selectedAppointment) return;
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/checkin/manual`, {
        appointment_id: selectedAppointment.appointment.id,
        patient_data: patientData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Check-in completato! Link di pagamento inviato al paziente.');
      setShowCheckinModal(false);
      setSelectedAppointment(null);
      loadData();
    } catch (error) {
      console.error('Error during manual check-in:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il check-in');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMarkReady = async (appointmentId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/mark-ready/${appointmentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Paziente segnato come pronto!');
      loadWaitingRoom();
    } catch (error) {
      console.error('Error marking ready:', error);
      toast.error('Errore');
    }
  };

  // Nuova funzione: Annulla check-in
  const handleCancelCheckin = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler annullare il check-in?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/checkin/cancel`, {
        appointment_id: appointmentId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Check-in annullato');
      loadData();
    } catch (error) {
      console.error('Error canceling check-in:', error);
      toast.error(error.response?.data?.detail || 'Errore annullamento check-in');
    }
  };

  // Nuova funzione: Check-in emergenza
  const handleEmergencyCheckin = async () => {
    if (!emergencyPatient.first_name || !emergencyPatient.last_name) {
      toast.error('Inserisci almeno nome e cognome');
      return;
    }
    
    if (emergencyPatient.services.length === 0) {
      toast.error('Seleziona almeno una prestazione');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/checkin/emergency`, emergencyPatient, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Check-in emergenza completato!');
      setEmergencyResult(response.data);
      setShowEmergencyModal(false);
      setShowQrResultModal(true);
      
      // Reset form
      setEmergencyPatient({ 
        first_name: '', 
        last_name: '', 
        phone: '', 
        email: '', 
        reason: 'Visita urgente',
        services: [],
        doctor_id: '',
        room: '',
        include_emergency_fee: true
      });
      loadData();
    } catch (error) {
      console.error('Error emergency check-in:', error);
      toast.error(error.response?.data?.detail || 'Errore check-in emergenza');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Toggle service selection
  const toggleService = (serviceId) => {
    setEmergencyPatient(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  // Calcola totale preventivo
  const calculateTotal = () => {
    let total = 0;
    emergencyPatient.services.forEach(serviceId => {
      const service = availableServices.find(s => s.id === serviceId);
      if (service) total += service.price;
    });
    if (emergencyPatient.include_emergency_fee) total += 50;
    return total;
  };

  // Nuova funzione: Cerca paziente esistente per check-in emergenza
  const searchExistingPatients = async (query) => {
    if (query.length < 2) {
      setPatientSearchResults([]);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/search-patients?q=${encodeURIComponent(query)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatientSearchResults(res.data.patients || []);
    } catch (error) {
      console.error('Error searching patients:', error);
    }
  };

  // Nuova funzione: Apri profilo paziente con appuntamenti
  const openPatientProfile = async (patient) => {
    setSelectedPatientProfile(patient);
    setShowPatientModal(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/patient/${patient.id}/appointments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatientAppointments(res.data.appointments || []);
    } catch (error) {
      console.error('Error loading patient appointments:', error);
      setPatientAppointments([]);
    }
  };

  const getStatusBadge = (apt) => {
    const status = apt.appointment?.status;
    const paymentStatus = apt.invoice?.status;
    
    if (status === 'in_progress') {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">In Visita</span>;
    }
    if (status === 'ready' || paymentStatus === 'paid') {
      return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Pronto</span>;
    }
    if (status === 'checked_in') {
      if (paymentStatus === 'paid') {
        return <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-700">Pagato - Pronto</span>;
      }
      return <span className="px-2 py-1 rounded-full text-xs bg-amber-100 text-amber-700">In Attesa Pagamento</span>;
    }
    if (status === 'confirmed') {
      return <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">Confermato</span>;
    }
    return <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700">Programmato</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin" style={{color: '#2A9FCB'}} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con QR Code e Bottone Emergenza */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-semibold" style={{color: '#2A9FCB', fontFamily: "'Lora', Georgia, serif"}}>
            Check-in Pazienti
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci accoglienza pazienti in studio
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowEmergencyModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:opacity-90"
            style={{background: 'linear-gradient(135deg, #EF4444, #DC2626)'}}
            data-testid="btn-emergency-checkin"
          >
            <Plus className="w-4 h-4" />
            Check-in Emergenza
          </button>
          <button
            onClick={() => setShowQrModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#2A9FCB] transition-colors hover:bg-[#E8F4FD]"
            style={{color: '#2A9FCB'}}
          >
            <QrCode className="w-4 h-4" />
            Mostra QR
          </button>
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all hover:opacity-90"
            style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
          >
            <RefreshCw className="w-4 h-4" />
            Aggiorna
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActiveTab('today')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'today' ? 'bg-white shadow-sm text-[#2A9FCB]' : 'text-gray-600'
          }`}
        >
          <Clock className="w-4 h-4" />
          Appuntamenti Oggi ({todayAppointments.length})
        </button>
        <button
          onClick={() => setActiveTab('waiting')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'waiting' ? 'bg-white shadow-sm text-[#2A9FCB]' : 'text-gray-600'
          }`}
        >
          <Users className="w-4 h-4" />
          Sala Attesa ({waitingRoom.length})
        </button>
      </div>

      {/* Today Appointments Tab */}
      {activeTab === 'today' && (
        <div className="space-y-3">
          {todayAppointments.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Clock className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessun appuntamento per oggi</p>
            </div>
          ) : (
            todayAppointments.map((apt) => (
              <div
                key={apt.appointment?.id}
                className="bg-white rounded-xl border p-4 hover:shadow-md transition-all"
              >
                <div className="flex flex-col sm:flex-row justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white"
                         style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}>
                      {apt.appointment?.appointment_date?.split('T')[1]?.substring(0, 5)}
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{color: '#2A9FCB'}}>
                        {apt.patient?.first_name} {apt.patient?.last_name}
                      </h3>
                      <p className="text-sm text-gray-500">{apt.appointment?.reason || 'Visita'}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                        {apt.patient?.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {apt.patient.phone}
                          </span>
                        )}
                        <span>Stanza {apt.appointment?.room || '1'}</span>
                        <span>Dott. {apt.doctor?.last_name}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    {getStatusBadge(apt)}
                    
                    {apt.appointment?.status === 'scheduled' || apt.appointment?.status === 'confirmed' ? (
                      <button
                        onClick={() => openManualCheckin(apt)}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg text-white text-sm"
                        style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                      >
                        <UserCheck className="w-4 h-4" />
                        Check-in
                      </button>
                    ) : apt.appointment?.status === 'checked_in' && apt.invoice?.status === 'paid' ? (
                      <button
                        onClick={() => handleMarkReady(apt.appointment.id)}
                        className="flex items-center gap-1 px-3 py-2 rounded-lg bg-green-500 text-white text-sm"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Pronto
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Waiting Room Tab */}
      {activeTab === 'waiting' && (
        <div className="space-y-3">
          {waitingRoom.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl">
              <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Nessun paziente in sala attesa</p>
            </div>
          ) : (
            waitingRoom.map((item, idx) => (
              <div
                key={item.appointment?.id || idx}
                className={`bg-white rounded-xl border p-4 ${item.is_ready ? 'border-green-300 bg-green-50/30' : ''} hover:shadow-md transition-all`}
              >
                <div className="flex justify-between items-center">
                  <div 
                    className="flex items-center gap-4 flex-1 cursor-pointer hover:bg-gray-50 rounded-lg p-2 -m-2"
                    onClick={() => openPatientProfile(item.patient)}
                    data-testid={`patient-row-${item.patient?.id}`}
                  >
                    <div className={`w-3 h-3 rounded-full ${item.is_ready ? 'bg-green-500' : 'bg-amber-500'} animate-pulse`} />
                    <div>
                      <h3 className="font-semibold flex items-center gap-2" style={{color: '#2A9FCB'}}>
                        {item.patient?.first_name} {item.patient?.last_name}
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </h3>
                      <p className="text-sm text-gray-500">
                        Check-in: {item.appointment?.checkin_time ? new Date(item.appointment.checkin_time).toLocaleTimeString('it-IT', {hour: '2-digit', minute: '2-digit'}) : '-'}
                        {' • '} Dott. {item.doctor?.last_name}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {item.is_ready ? (
                      <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-green-100 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        Pronto
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm bg-amber-100 text-amber-700">
                        <CreditCard className="w-4 h-4" />
                        Attesa pagamento
                      </span>
                    )}
                    
                    {/* Bottone Annulla Check-in */}
                    <button
                      onClick={() => handleCancelCheckin(item.appointment?.id)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors"
                      title="Annulla check-in"
                      data-testid={`btn-cancel-checkin-${item.appointment?.id}`}
                    >
                      <XCircle className="w-4 h-4" />
                      Annulla
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* QR Code Modal */}
      {showQrModal && qrCode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-8 text-center">
            <h3 className="text-xl font-semibold mb-4" style={{color: '#2A9FCB'}}>
              QR Code Check-in
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Il paziente può scansionare questo codice per effettuare il check-in autonomamente
            </p>
            <img 
              src={qrCode.qr_code} 
              alt="QR Code Check-in" 
              className="w-64 h-64 mx-auto mb-4"
            />
            <p className="text-xs text-gray-400 mb-6 break-all">{qrCode.checkin_url}</p>
            <button
              onClick={() => setShowQrModal(false)}
              className="px-6 py-2 rounded-lg border hover:bg-gray-50"
            >
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Manual Check-in Modal */}
      {showCheckinModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>
                Check-in Manuale
              </h3>
              <button
                onClick={() => setShowCheckinModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 rounded-xl" style={{background: '#E8F4FD'}}>
                <p className="text-sm" style={{color: '#2A9FCB'}}>
                  <strong>Appuntamento:</strong> {selectedAppointment.appointment?.appointment_date?.split('T')[1]?.substring(0, 5)} - {selectedAppointment.appointment?.reason}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
                  <input
                    type="text"
                    value={patientData.first_name}
                    onChange={(e) => setPatientData({...patientData, first_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome</label>
                  <input
                    type="text"
                    value={patientData.last_name}
                    onChange={(e) => setPatientData({...patientData, last_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                <input
                  type="tel"
                  value={patientData.phone}
                  onChange={(e) => setPatientData({...patientData, phone: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={patientData.email}
                  onChange={(e) => setPatientData({...patientData, email: e.target.value})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                <input
                  type="text"
                  value={patientData.fiscal_code}
                  onChange={(e) => setPatientData({...patientData, fiscal_code: e.target.value.toUpperCase()})}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB] font-mono"
                  maxLength={16}
                />
              </div>

              <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Confermando il check-in, verrà inviato automaticamente un SMS con il link di pagamento al paziente.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowCheckinModal(false)}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleManualCheckin}
                  disabled={isSubmitting}
                  className="flex-1 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Confermo...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Conferma Check-in
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Check-in Modal */}
      {showEmergencyModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold text-red-600">
                Check-in Emergenza
              </h3>
              <button
                onClick={() => setShowEmergencyModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                &times;
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <p className="text-sm text-red-800">
                    Paziente senza appuntamento. Seleziona prestazioni e medico.
                  </p>
                </div>
              </div>

              {/* Cerca paziente esistente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cerca paziente esistente</label>
                <input
                  type="text"
                  placeholder="Cerca per nome, cognome o telefono..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    searchExistingPatients(e.target.value);
                  }}
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                />
                {patientSearchResults.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-32 overflow-y-auto">
                    {patientSearchResults.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setEmergencyPatient({
                            ...emergencyPatient,
                            patient_id: p.id,
                            first_name: p.first_name || p.nome,
                            last_name: p.last_name || p.cognome,
                            phone: p.phone || p.telefono,
                            email: p.email
                          });
                          setSearchQuery('');
                          setPatientSearchResults([]);
                        }}
                        className="w-full text-left p-3 hover:bg-gray-50 border-b last:border-b-0"
                      >
                        <p className="font-medium">{p.first_name || p.nome} {p.last_name || p.cognome}</p>
                        <p className="text-sm text-gray-500">{p.phone || p.telefono}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    type="text"
                    value={emergencyPatient.first_name}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, first_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                  <input
                    type="text"
                    value={emergencyPatient.last_name}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, last_name: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                  <input
                    type="tel"
                    value={emergencyPatient.phone}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, phone: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                    placeholder="+39..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                  <input
                    type="text"
                    value={emergencyPatient.reason}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, reason: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  />
                </div>
              </div>

              {/* Selezione Medico e Stanza */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Stethoscope className="w-4 h-4 inline mr-1" />
                    Medico
                  </label>
                  <select
                    value={emergencyPatient.doctor_id}
                    onChange={(e) => {
                      const doc = availableDoctors.find(d => d.id === e.target.value);
                      setEmergencyPatient({
                        ...emergencyPatient, 
                        doctor_id: e.target.value,
                        room: doc?.room || ''
                      });
                    }}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  >
                    <option value="">Seleziona medico...</option>
                    {availableDoctors.map(d => (
                      <option key={d.id} value={d.id}>
                        Dr. {d.last_name} {d.room ? `(Stanza ${d.room})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <Building2 className="w-4 h-4 inline mr-1" />
                    Stanza
                  </label>
                  <select
                    value={emergencyPatient.room}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, room: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#2A9FCB]"
                  >
                    <option value="">Auto</option>
                    <option value="A">Stanza A</option>
                    <option value="B">Stanza B</option>
                    <option value="C">Stanza C</option>
                    <option value="D">Stanza D</option>
                  </select>
                </div>
              </div>

              {/* Selezione Prestazioni */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prestazioni da erogare *
                </label>
                <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1">
                  {availableServices.map(service => (
                    <label
                      key={service.id}
                      className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-colors ${
                        emergencyPatient.services.includes(service.id)
                          ? 'bg-sky-50 border border-sky-200'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={emergencyPatient.services.includes(service.id)}
                          onChange={() => toggleService(service.id)}
                          className="w-4 h-4 text-sky-600 rounded"
                        />
                        <span className="text-sm">{service.name}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {service.price.toFixed(2)} EUR
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Supplemento Emergenza */}
              <label className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200 cursor-pointer">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={emergencyPatient.include_emergency_fee}
                    onChange={(e) => setEmergencyPatient({...emergencyPatient, include_emergency_fee: e.target.checked})}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm font-medium text-red-800">Supplemento Emergenza</span>
                </div>
                <span className="text-sm font-bold text-red-600">+50.00 EUR</span>
              </label>

              {/* Riepilogo Totale */}
              {emergencyPatient.services.length > 0 && (
                <div className="p-4 rounded-xl bg-gray-50 border">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Totale Preventivo:</span>
                    <span className="text-xl font-bold" style={{color: '#2A9FCB'}}>
                      {calculateTotal().toFixed(2)} EUR
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {emergencyPatient.services.length} prestazione/i selezionata/e
                    {emergencyPatient.include_emergency_fee && ' + supplemento emergenza'}
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowEmergencyModal(false)}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  onClick={handleEmergencyCheckin}
                  disabled={isSubmitting || !emergencyPatient.first_name || !emergencyPatient.last_name || emergencyPatient.services.length === 0}
                  className="flex-1 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{background: 'linear-gradient(135deg, #EF4444, #DC2626)'}}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processo...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4" />
                      Genera QR e Preventivo
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Result Modal - Mostra QR dopo check-in emergenza */}
      {showQrResultModal && emergencyResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6 text-center">
            <h3 className="text-xl font-semibold mb-2" style={{color: '#2A9FCB'}}>
              Check-in Completato
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Mostra questo QR code al paziente per il pagamento
            </p>
            
            <div className="bg-white p-4 rounded-xl inline-block mb-4 shadow-lg">
              <QRCodeSVG 
                value={emergencyResult.payment_url} 
                size={200}
                level="H"
              />
            </div>
            
            <div className="text-left space-y-2 mb-4 p-4 bg-gray-50 rounded-xl">
              <p className="text-sm"><strong>Paziente:</strong> {emergencyPatient.first_name} {emergencyPatient.last_name}</p>
              <p className="text-sm"><strong>Medico:</strong> {emergencyResult.doctor?.name} - Stanza {emergencyResult.doctor?.room}</p>
              <p className="text-sm"><strong>Totale:</strong> <span className="font-bold text-lg">{emergencyResult.summary?.total?.toFixed(2)} EUR</span></p>
              {emergencyResult.summary?.subscription_discount > 0 && (
                <p className="text-sm text-green-600">
                  <strong>Sconto abbonamento:</strong> -{emergencyResult.summary.subscription_discount.toFixed(2)} EUR
                </p>
              )}
              <p className="text-sm font-bold" style={{color: '#2A9FCB'}}>
                <strong>Da pagare:</strong> {emergencyResult.summary?.amount_to_pay?.toFixed(2)} EUR
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setShowQrResultModal(false);
                    setEmergencyResult(null);
                  }}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50"
                >
                  Chiudi
                </button>
                <button
                  onClick={() => window.open(emergencyResult.payment_url, '_blank')}
                  className="flex-1 py-3 rounded-lg font-medium text-white"
                  style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                >
                  Apri Link
                </button>
              </div>
              
              {/* Invio Link via SMS/Email */}
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    await axios.post(`${API}/checkin/send-payment-link`, {
                      appointment_id: emergencyResult.appointment_id,
                      payment_url: emergencyResult.payment_url,
                      patient_name: `${emergencyPatient.first_name} ${emergencyPatient.last_name}`,
                      phone: emergencyPatient.phone,
                      email: emergencyPatient.email,
                      amount: emergencyResult.summary?.amount_to_pay || 0
                    }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    toast.success('Link pagamento inviato via SMS ed Email!');
                  } catch (error) {
                    toast.error('Errore nell\'invio del link');
                  }
                }}
                className="w-full py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <Phone className="w-4 h-4" />
                <Mail className="w-4 h-4" />
                Invia Link di Pagamento (SMS + Email)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Patient Profile Modal */}
      {showPatientModal && selectedPatientProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                     style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}>
                  {selectedPatientProfile?.first_name?.[0]}{selectedPatientProfile?.last_name?.[0]}
                </div>
                <div>
                  <h3 className="text-xl font-semibold" style={{color: '#2A9FCB'}}>
                    {selectedPatientProfile?.first_name} {selectedPatientProfile?.last_name}
                  </h3>
                  <p className="text-sm text-gray-500">{selectedPatientProfile?.phone}</p>
                </div>
              </div>
              <button
                onClick={() => setShowPatientModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Info Paziente */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-medium">{selectedPatientProfile?.email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Codice Fiscale</p>
                  <p className="font-medium font-mono">{selectedPatientProfile?.fiscal_code || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Data di Nascita</p>
                  <p className="font-medium">{selectedPatientProfile?.birth_date || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Indirizzo</p>
                  <p className="font-medium">{selectedPatientProfile?.address || '-'}</p>
                </div>
              </div>

              {/* Appuntamenti */}
              <div>
                <h4 className="font-semibold flex items-center gap-2 mb-4" style={{color: '#2A9FCB'}}>
                  <Calendar className="w-5 h-5" />
                  Appuntamenti Programmati
                </h4>
                
                {patientAppointments.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Nessun appuntamento trovato</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {patientAppointments.map((apt) => {
                      const aptDate = new Date(apt.appointment_date);
                      const isPast = aptDate < new Date();
                      const isToday = aptDate.toDateString() === new Date().toDateString();
                      
                      return (
                        <div
                          key={apt.id}
                          className={`p-3 rounded-lg border ${
                            isToday ? 'bg-sky-50 border-sky-200' : 
                            isPast ? 'bg-gray-50 border-gray-200' : 
                            'bg-white border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <div>
                              <p className="font-medium">
                                {aptDate.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                {' '}{aptDate.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                              </p>
                              <p className="text-sm text-gray-500">{apt.reason || 'Visita'}</p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              apt.status === 'completed' ? 'bg-green-100 text-green-700' :
                              apt.status === 'checked_in' ? 'bg-amber-100 text-amber-700' :
                              apt.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {apt.status === 'completed' ? 'Completato' :
                               apt.status === 'checked_in' ? 'In attesa' :
                               apt.status === 'confirmed' ? 'Confermato' :
                               apt.status === 'cancelled' ? 'Annullato' : 'Programmato'}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowPatientModal(false)}
                  className="flex-1 py-3 border rounded-lg font-medium hover:bg-gray-50"
                >
                  Chiudi
                </button>
                <button
                  onClick={() => {
                    window.open(`/admin/paziente/${selectedPatientProfile?.id}`, '_blank');
                  }}
                  className="flex-1 py-3 rounded-lg font-medium text-white flex items-center justify-center gap-2"
                  style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                >
                  <Eye className="w-4 h-4" />
                  Apri Cartella Clinica
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckInManager;
